// @flow

// Types
import type {
  UserOperation,
} from '../../types'

import type {
  User,
} from 'fc-model-users/types'

import {
  PROP_ID,
  PROP_STATUS,
  PROP_FIRST,
  PROP_LAST,
  PROP_PHONE,
  PROP_CONTACTS,
  PROP_CONTACT_TYPE_PHONE,
} from 'fc-model-users'

// Libs
import _find from 'lodash/find'
import _every from 'lodash/every'
import _pick from 'lodash/pick'
import _map from 'lodash/map'
import _get from 'lodash/get'
import _isEqual from 'lodash/isEqual'

// Operator
import operator from '../operator'

// Controller
import controller from 'fc-ctrl-users'

// Selector
import { users$ } from 'fc-users'

// Effect Managers
import { call, fork, put, race, select, take } from 'redux-saga/effects'

import { getToken } from 'fc-auth'
import url from 'hfty-utils/url'

type OperationsArgs = {
  operations$: (state: Object) => Array<UserOperation>
}

// Exports
export default function *watchOperations ({operations$}: OperationsArgs): Generator<*, void, *> {
  while (true) {
    yield take([operator.actionTypes.add(), operator.actionTypes.edit(), operator.actionTypes.delete()])
    yield fork(handleOperations, {operations$})
  }
}

export function *handleOperations ({operations$}: OperationsArgs): Generator<*, void, *> {
  let done = false
  while (!done) {
    const effect = yield race({
      submit: take(operator.actionTypes.submitOperations()),
      cancel: take(operator.actionTypes.cancelOperations()),
    })

    if (effect.submit) {
      done = yield call(handleSubmitOperations, {operations$})
    } else {
      done = true // Cancelled
    }
  }
}

export function *handleSubmitOperations ({operations$}: OperationsArgs): Generator<*, boolean, *> {
  const operations = yield select(operations$)
  const nextOperations: Array<UserOperation> = yield _map(operations, function (operation: UserOperation): Object {
    return call(handleSubmitOperation, operation)
  })

  return _every(nextOperations, function (operation: UserOperation): boolean {
    return operation.success
  })
}

export function *handleSubmitOperation (operation: UserOperation): Generator<*, UserOperation, *> {
  let model: User = {
    ...operation.model,
    user: {
      ...operation.model.user,
      email: operation.model.email,
    },
  }
  let prunedModel: User = model
  let userId = model[PROP_ID]

  if (operation.operation === 'DELETE') {
    model[PROP_STATUS] = 'RETIRED'
    prunedModel = _pick(model, [PROP_STATUS])
  } else if (operation.operation === 'EDIT') {
    prunedModel = _pick(model, [PROP_FIRST, PROP_LAST, 'user'])
    // if phone number is edited on newly created user,
    // major workaround required to get contacts object because /client does not return contacts in response body
    const users = yield select(users$)
    const userData = _find(users, {[PROP_ID]: userId})
    const oldPhone = _get(userData, 'contacts.phone.entry', null) || userData[PROP_PHONE]
    if (!_isEqual(oldPhone, model[PROP_PHONE])) {
      let token = yield call(getToken)
      let phoneId = _get(userData, 'contacts.id', null)
      if (!phoneId) {
        const clientData = yield call(fetch, url({
          path: '/hefty/client/?status=ACTIVE',
        }), {
          headers: {
            Authorization: `Token ${token}`,
          },
        })
        const parseClientData = yield call([clientData, clientData.json])
        const updatedClientData = _find(parseClientData, { id: userId })
        const contactData = _find(updatedClientData[PROP_CONTACTS], {type: PROP_CONTACT_TYPE_PHONE})
        phoneId = _get(contactData, 'id', null)
      }
      if (phoneId) {
        let formData = new FormData()
        formData.append('id', phoneId)
        formData.append('entry', model[PROP_PHONE])
        formData.append('type', PROP_CONTACT_TYPE_PHONE)
        const phoneResponse = yield call(fetch, url({
          path: 'contact/',
          id: phoneId,
        }), {
          method: 'PUT',
          body: formData,
          headers: {
            Authorization: `Token ${token}`,
          },
        })
        yield call([phoneResponse, phoneResponse.json])
      }
    }
  }

  const { error, response, model: updatedUser } = operation.operation === 'ADD'
    ? yield call([controller, controller.create], { model: prunedModel }, {meta: {encode: false, reduce: false}})
    : yield call([controller, controller.update],
      { model: prunedModel, id: userId },
      {meta: {encode: false, reduce: false}})

  let returnObject = null

  if (!error && operation.operation !== 'DELETE') {
    userId = userId || updatedUser[PROP_ID]

    const operationData = {
      id: userId,
      data: {
        [userId]: {
          ...updatedUser,
          phone: model[PROP_PHONE],
        },
      },
    }

    yield put(controller.actionCreators.receive(operationData))

    if (operation.operation === 'ADD') {
      let token = yield call(getToken)
      let formData = new FormData()
      formData.append('client', userId)

      yield call(fetch, url({
        path: 'hefty/duplicateclientbillingaddress/',
      }), {
        method: 'POST',
        body: formData,
        headers: {
          Authorization: `Token ${token}`,
        },
      })
    }
  }

  if (error || (returnObject && returnObject.error)) {
    const nextOperation = {
      ...operation,
      submitting: false,
      success: false,
      errors: error ? response : returnObject.response,
    }

    const action = operator.actionCreators.updateOperation(nextOperation)
    yield put(action)

    return nextOperation
  } else {
    const nextOperation = {
      ...operation,
      submitting: false,
      success: true,
    }
    yield put(operator.actionCreators.updateOperation(nextOperation))
    const action = operator.actionCreators.cancelOperation(nextOperation)

    if (operation.operation === 'DELETE') {
      yield put(controller.actionCreators.deleted({id: userId}))
    }

    yield put(action)

    return nextOperation
  }
}
